import { getTileUrl, getTileAttribution } from "../../utils/map-utils";
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, Skeleton, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Tabs, Tab, Divider,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { Search, Close, Phone, Email, LocationOn, People } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Treemap } from 'recharts';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { especializadaService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';

// @ts-ignore
delete (L.Icon.Default.prototype as any)._getIconUrl;

const createIcon = (color: string) => new L.DivIcon({
  className: '',
  html: `<div style="width:22px;height:22px;background:${color};border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.35)"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 22], popupAnchor: [0, -22],
});

const TYPE_COLORS: Record<string, string> = {
  'HOSPITAL GERAL': '#ef4444',
  'HOSPITAL ESPECIALIZADO': '#dc2626',
  'POLICLINICA': '#2563eb',
  'AMBULATORIO DE HOSPITAL GERAL': '#16a34a',
  'AMBULATORIO DE HOSPITAL ESPECIALIZADO': '#15803d',
  'CLINICA ESPECIALIZADA/AMBULATORIO DE ESPECIALIDADE': '#7c3aed',
  'UNIDADE AMBULATORIAL DE ESPECIALIDADE(S)': '#0891b2',
};

const EspecializadaRede: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');
  const [search, setSearch] = useState('');
  const [selectedUnit, setSelectedUnit] = useState<any>(null);
  const [tabValue, setTabValue] = useState(0);
  const [profSearch, setProfSearch] = useState('');
  const [profUnidade, setProfUnidade] = useState<string>('');
  const [profEspecialidade, setProfEspecialidade] = useState<string>('');

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    setLoading(true);
    setError(null);
    especializadaService.getRede(municipioAtivo.id, dataInicio || undefined, dataFim || undefined)
      .then(setData)
      .catch((err: any) => setError(err.response?.data?.message || 'Erro ao carregar dados'))
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  const filteredUnidades = useMemo(() => {
    if (!data?.unidades) return [];
    if (!search) return data.unidades;
    const s = search.toLowerCase();
    return data.unidades.filter((u: any) =>
      (u.nome_unidade || '').toLowerCase().includes(s) ||
      (u.nome_tipo_unidade || '').toLowerCase().includes(s)
    );
  }, [data?.unidades, search]);

  const mapUnidades = useMemo(() => {
    return (data?.unidades || []).filter((u: any) => u.latitude && u.longitude);
  }, [data?.unidades]);

  const dadosContexto = useMemo(() => {
    if (!data) return '';
    return `Rede Especializada - ${municipioAtivo?.nome || 'Contagem/MG'}
Total unidades: ${data.kpis?.total_unidades || 0}
Total profissionais: ${data.kpis?.total_profissionais || 0}
Atendimentos no periodo: ${Number(data.kpis?.total_atendimentos || 0).toLocaleString('pt-BR')}
Tipos: ${(data.servicosPorTipo || []).map((s: any) => s.nome_tipo_unidade + ': ' + s.total_unidades).join(', ')}`;
  }, [data, municipioAtivo]);

  const getUnitProduction = (codigoUnidade: number) => {
    return data?.producaoPorUnidade?.find((p: any) => p.codigo_unidade === codigoUnidade);
  };

  const getUnitEquipes = (codigoUnidade: number) => {
    return (data?.equipes || []).filter((e: any) => e.codigo_unidade === codigoUnidade);
  };

  const profissionaisUnidades = useMemo(() => {
    const set = new Map<string, string>();
    (data?.equipes || []).forEach((e: any) => {
      if (e.codigo_unidade && e.nome_unidade) set.set(String(e.codigo_unidade), e.nome_unidade);
    });
    return Array.from(set.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [data?.equipes]);

  const profissionaisEspecialidades = useMemo(() => {
    const set = new Set<string>();
    (data?.equipes || []).forEach((e: any) => { if (e.especialidade) set.add(e.especialidade); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data?.equipes]);

  const profissionaisFiltrados = useMemo(() => {
    const lista: any[] = data?.equipes || [];
    const s = profSearch.trim().toLowerCase();
    return lista.filter((e) => {
      if (s && !(e.nome_profissional || '').toLowerCase().includes(s)) return false;
      if (profUnidade && String(e.codigo_unidade) !== profUnidade) return false;
      if (profEspecialidade && e.especialidade !== profEspecialidade) return false;
      return true;
    });
  }, [data?.equipes, profSearch, profUnidade, profEspecialidade]);

  const treemapData = useMemo(() => {
    if (!data?.servicosPorTipo) return [];
    return data.servicosPorTipo.map((s: any) => ({
      name: (s.nome_tipo_unidade || '').replace(/AMBULATORIO DE /g, 'AMB. ').substring(0, 35),
      size: Number(s.total_atendimentos),
      unidades: Number(s.total_unidades),
    }));
  }, [data]);

  if (loading) {
    return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={500} /></Box>;
  }

  if (error) {
    return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Rede Especializada</Typography>
          <Typography variant="body2" color="text.secondary">Identificação de unidades, equipes e serviços</Typography>
        </Box>
        <DateRangeSelector dataInicio={dataInicio} dataFim={dataFim} onChange={(di, df) => { setDataInicio(di); setDataFim(df); }} />
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}><KpiCard titulo="Unidades" valor={data?.kpis?.total_unidades || 0} cor="#2563eb" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Profissionais" valor={data?.kpis?.total_profissionais || 0} cor="#16a34a" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Especialidades" valor={data?.kpis?.total_especialidades_equipe || 0} cor="#7c3aed" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Atendimentos" valor={Number(data?.kpis?.total_atendimentos || 0).toLocaleString('pt-BR')} cor="#ea580c" /></Grid>
      </Grid>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label="Mapa" />
        <Tab label="Tabela" />
        <Tab label="Serviços" />
        <Tab label="Profissionais" />
      </Tabs>

      {/* Mapa */}
      {tabValue === 0 && (
        <Paper sx={{ p: 0, overflow: 'hidden', borderRadius: 2, mb: 2 }}>
          {mapUnidades.length > 0 ? (
            <MapContainer center={[-19.93, -44.05]} zoom={12} style={{ height: 500 }}>
              <TileLayer url={getTileUrl()} attribution={getTileAttribution()} />
              {mapUnidades.map((u: any) => (
                <Marker key={u.codigo_unidade} position={[u.latitude, u.longitude]} icon={createIcon(TYPE_COLORS[u.nome_tipo_unidade] || '#6b7280')}
                  eventHandlers={{ click: () => setSelectedUnit(u) }}>
                  <Popup>
                    <strong>{u.nome_unidade}</strong><br />
                    <small>{u.nome_tipo_unidade}</small><br />
                    {u.endereco && <small>{u.endereco}</small>}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          ) : (
            <Alert severity="info" sx={{ m: 2 }}>Nenhuma unidade com geolocalização disponível.</Alert>
          )}
          <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(TYPE_COLORS).map(([type, color]) => (
              <Chip key={type} size="small" label={type.substring(0, 25)} sx={{ bgcolor: color + '20', color, fontWeight: 500, fontSize: '0.7rem' }} />
            ))}
          </Box>
        </Paper>
      )}

      {/* Tabela */}
      {tabValue === 1 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField size="small" placeholder="Buscar unidade..." value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ mb: 2, width: 300 }}
          />
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Unidade</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>CNES</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Atendimentos</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUnidades.map((u: any) => {
                  const prod = getUnitProduction(u.codigo_unidade);
                  return (
                    <TableRow key={u.codigo_unidade} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedUnit(u)}>
                      <TableCell>{u.nome_unidade}</TableCell>
                      <TableCell><Chip size="small" label={u.nome_tipo_unidade?.substring(0, 20)} sx={{ bgcolor: (TYPE_COLORS[u.nome_tipo_unidade] || '#6b7280') + '15', color: TYPE_COLORS[u.nome_tipo_unidade] || '#6b7280', fontWeight: 500, fontSize: '0.7rem' }} /></TableCell>
                      <TableCell>{u.codcnes}</TableCell>
                      <TableCell align="right">{prod ? Number(prod.total_atendimentos).toLocaleString('pt-BR') : '-'}</TableCell>
                      <TableCell><Button size="small" onClick={(e) => { e.stopPropagation(); setSelectedUnit(u); }}>Detalhes</Button></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Treemap Serviços */}
      {tabValue === 2 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Serviços por Tipo de Unidade</Typography>
          <ResponsiveContainer width="100%" height={400}>
            <Treemap data={treemapData} dataKey="size" nameKey="name" stroke="#fff" fill="#2563eb"
              content={({ x, y, width, height, name, size }: any) => {
                if (width < 40 || height < 30) return <g />;
                return (
                  <g>
                    <rect x={x} y={y} width={width} height={height} fill="#2563eb" fillOpacity={0.8} stroke="#fff" />
                    <text x={x + 6} y={y + 16} fill="#fff" fontSize={10} fontWeight={600}>{name}</text>
                    <text x={x + 6} y={y + 30} fill="#fff" fontSize={9} opacity={0.8}>{Number(size).toLocaleString('pt-BR')}</text>
                  </g>
                );
              }}
            />
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Aba Profissionais */}
      {tabValue === 3 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <People fontSize="small" color="primary" />
            <Typography variant="subtitle1" fontWeight={600} sx={{ mr: 2 }}>
              Identificação dos Profissionais da Rede
            </Typography>
            <Chip
              size="small"
              label={`${profissionaisFiltrados.length} de ${(data?.equipes || []).length}`}
              color="primary"
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Buscar profissional..."
              value={profSearch}
              onChange={(e) => setProfSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              sx={{ width: 280 }}
            />
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Unidade</InputLabel>
              <Select value={profUnidade} label="Unidade" onChange={(e) => setProfUnidade(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                {profissionaisUnidades.map(([cod, nome]) => (
                  <MenuItem key={cod} value={cod}>{(nome || '').substring(0, 50)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Especialidade</InputLabel>
              <Select value={profEspecialidade} label="Especialidade" onChange={(e) => setProfEspecialidade(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                {profissionaisEspecialidades.map((esp) => (
                  <MenuItem key={esp} value={esp}>{esp}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {(profSearch || profUnidade || profEspecialidade) && (
              <Button size="small" onClick={() => { setProfSearch(''); setProfUnidade(''); setProfEspecialidade(''); }}>
                Limpar
              </Button>
            )}
          </Box>

          <TableContainer sx={{ maxHeight: 560 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Profissional</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>CBO</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Especialidade</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Equipe</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo Equipe</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Unidade</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">CH (h)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profissionaisFiltrados.map((p: any, i: number) => (
                  <TableRow key={`${p.codigo_unidade}-${p.nome_profissional}-${i}`} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{p.nome_profissional}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{p.cbo}</TableCell>
                    <TableCell>{p.especialidade}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{p.nome_equipe}</TableCell>
                    <TableCell>
                      <Chip size="small" label={p.tipo_equipe} variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{(p.nome_unidade || '').substring(0, 40)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{p.carga_horaria}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={p.ativo ? 'Ativo' : 'Inativo'}
                        color={p.ativo ? 'success' : 'default'}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {profissionaisFiltrados.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nenhum profissional encontrado com os filtros aplicados.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Dialog Detalhes da Unidade */}
      <Dialog open={!!selectedUnit} onClose={() => setSelectedUnit(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedUnit?.nome_unidade}
          <IconButton onClick={() => setSelectedUnit(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedUnit && (
            <Box>
              <Chip size="small" label={selectedUnit.nome_tipo_unidade} color="primary" sx={{ mb: 2 }} />
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">CNES</Typography><Typography variant="body2" fontWeight={600}>{selectedUnit.codcnes}</Typography></Grid>
                {selectedUnit.endereco && <Grid item xs={12}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LocationOn fontSize="small" color="action" /><Typography variant="body2">{selectedUnit.endereco}</Typography></Box></Grid>}
                {selectedUnit.telefone && <Grid item xs={6}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Phone fontSize="small" color="action" /><Typography variant="body2">{selectedUnit.telefone}</Typography></Box></Grid>}
                {selectedUnit.email && <Grid item xs={6}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Email fontSize="small" color="action" /><Typography variant="body2">{selectedUnit.email}</Typography></Box></Grid>}
                {selectedUnit.latitude && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Coordenadas: {selectedUnit.latitude}, {selectedUnit.longitude}</Typography></Grid>}
              </Grid>
              {(() => {
                const prod = getUnitProduction(selectedUnit.codigo_unidade);
                if (prod) return (
                  <Box sx={{ mb: 2 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Produção no Período</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={4}><Typography variant="caption" color="text.secondary">Atendimentos</Typography><Typography variant="body2" fontWeight={600}>{Number(prod.total_atendimentos).toLocaleString('pt-BR')}</Typography></Grid>
                      <Grid item xs={4}><Typography variant="caption" color="text.secondary">Especialidades</Typography><Typography variant="body2" fontWeight={600}>{prod.total_especialidades}</Typography></Grid>
                      <Grid item xs={4}><Typography variant="caption" color="text.secondary">Procedimentos</Typography><Typography variant="body2" fontWeight={600}>{prod.total_procedimentos}</Typography></Grid>
                    </Grid>
                  </Box>
                );
                return null;
              })()}
              {(() => {
                const equipes = getUnitEquipes(selectedUnit.codigo_unidade);
                if (equipes.length > 0) return (
                  <Box>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom><People fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />Equipes ({equipes.length} profissionais)</Typography>
                    <Table size="small">
                      <TableHead><TableRow><TableCell sx={{ fontWeight: 600 }}>Profissional</TableCell><TableCell sx={{ fontWeight: 600 }}>Especialidade</TableCell><TableCell sx={{ fontWeight: 600 }}>CH</TableCell></TableRow></TableHead>
                      <TableBody>
                        {equipes.map((e: any, i: number) => (
                          <TableRow key={i}><TableCell>{e.nome_profissional}</TableCell><TableCell>{e.especialidade}</TableCell><TableCell>{e.carga_horaria}h</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                );
                return null;
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setSelectedUnit(null)}>Fechar</Button></DialogActions>
      </Dialog>

      <ChatIAButton contexto="dashboard" dadosContexto={dadosContexto} />
    </Box>
  );
};

export default EspecializadaRede;
